import { useEffect, useState } from 'react'
import { CheckCircle, Loader2 } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatCurrency } from '../buchhaltung/invoiceUtils'
import {
  expectedPayment,
  hasSkonto,
  localIsoDate,
  skontoValidOn,
  type OutgoingRow,
} from './outgoingUtils'

interface Props {
  invoice: OutgoingRow
  onConfirm: (paidAt: string, applySkonto: boolean) => void
  pending: boolean
}

/**
 * "Bezahlt" direkt aus der Liste: Zahlungsdatum (Default heute) und ob der
 * Kunde Skonto gezogen hat. Den Betrag rechnet der Server (markInvoicesPaidBatch),
 * hier steht er nur zur Kontrolle. Teilzahlungen gehen ueber das Detail-Fenster.
 */
export default function MarkPaidPopover({ invoice, onConfirm, pending }: Props) {
  const today = localIsoDate()
  const [open, setOpen] = useState(false)
  const [paidAt, setPaidAt] = useState(today)
  const [withSkonto, setWithSkonto] = useState(false)

  // Beim Oeffnen frisch vorbelegen. Skonto-Haken nur, wenn die Frist am
  // Zahlungstag noch lief — sonst haette der Kunde es nicht ziehen duerfen.
  useEffect(() => {
    if (!open) return
    setPaidAt(today)
    setWithSkonto(skontoValidOn(invoice, today))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const skontoAllowed = skontoValidOn(invoice, paidAt)
  useEffect(() => {
    if (!skontoAllowed) setWithSkonto(false)
  }, [skontoAllowed])

  // Eingang vor dem Rechnungsdatum ist fast immer ein Tippfehler im Jahr
  const beforeInvoice = !!paidAt && !!invoice.invoice_date && paidAt < invoice.invoice_date
  const dateInvalid = !paidAt || paidAt > today || beforeInvoice
  const amount = expectedPayment(invoice, withSkonto && skontoAllowed)

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
        >
          <CheckCircle className="h-3.5 w-3.5" /> Bezahlt
        </button>
      </PopoverTrigger>
      {/* Klicks im Portal laufen im React-Baum bis zur Tabellenzeile hoch und
          wuerden sonst zusaetzlich das Detail-Fenster oeffnen. */}
      <PopoverContent align="end" className="w-80 bg-white" onClick={(e) => e.stopPropagation()}>
        <p className="mb-3 text-sm font-semibold text-gray-900">Zahlungseingang erfassen</p>

        <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor={`paid-at-${invoice.id}`}>
          Zahlungsdatum
        </label>
        <input
          id={`paid-at-${invoice.id}`}
          type="date"
          value={paidAt}
          max={today}
          min={invoice.invoice_date || undefined}
          onChange={(e) => setPaidAt(e.target.value)}
          className="mb-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
        />

        <p className="mb-3 min-h-[1rem] text-xs text-red-600">
          {paidAt > today ? 'Zahlungsdatum liegt in der Zukunft' : beforeInvoice ? 'Zahlungsdatum liegt vor dem Rechnungsdatum' : ''}
        </p>

        {hasSkonto(invoice) && (
          <label className={`mb-3 flex items-start gap-2 text-sm ${skontoAllowed ? 'text-gray-700' : 'text-gray-400'}`}>
            <input
              type="checkbox"
              checked={withSkonto && skontoAllowed}
              disabled={!skontoAllowed}
              onChange={(e) => setWithSkonto(e.target.checked)}
              className="mt-0.5 rounded text-primary-500"
            />
            <span>
              Kunde hat Skonto gezogen (−{formatCurrency(invoice.skonto_amount)})
              {!skontoAllowed && <span className="block text-xs">Skontofrist war zum Zahlungsdatum abgelaufen</span>}
            </span>
          </label>
        )}

        <div className="mb-3 flex items-baseline justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm">
          <span className="text-gray-600">Eingang</span>
          <span className="font-semibold tabular-nums text-gray-900">{formatCurrency(amount)}</span>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={dateInvalid || pending}
            onClick={() => {
              onConfirm(paidAt, withSkonto && skontoAllowed)
              setOpen(false)
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Als bezahlt markieren
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
